import React, { useState } from 'react';
import cx from 'classnames';
import { find } from 'lodash';
import moment from 'moment-timezone';

import { ModalProvider, useModalContext } from '@magoosh/context/modal_context';
import CardLayout from '@magoosh/layouts/card';
import { SecondaryButton, TabButton } from '@magoosh/lib/button';
import { Select } from '@magoosh/lib/forms';
import {
  FontAwesomeIcon,
  LightBulbPlantIcon,
  MagnifyingGlassIcon,
  WateringSeedlingIcon
} from '@magoosh/lib/icons';
import useQuery from '@magoosh/utils/use_query';
import { paths } from 'config/path_helpers';

import AnswersTable from './answers_table';
import PerformanceBreakdown from './performance_breakdown';
import ScoreCard from './score_card';
import UpsellCard from '../shared/upsell_card';
import { ScoredExamSection } from './types';
import ViewingSharedResultsBanner from './viewing_shared_results_banner';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  branding: {
    accessLevel: 'trial' | 'premium';
    examName: string;
    saleMessage: string;
  };
  mockTest: {
    finishedAt: number;
    id: number;
    noScoreReason: string;
    score: string;
    scoredExamSections: ScoredExamSection[];
    shareableId: string;
    template: {
      name: string;
    };
    user: {
      name: string;
    }
  };
  ui?: {
    isViewingSharedResults?: boolean;
  }
}

const ShowMockTest: React.FC<Props> = (props) => {
  const breadcrumbs = [
    { name: 'Home', path: paths.dashboard() },
    { name: 'Practice Tests', path: paths.practiceTests() },
    { name: 'Results' }
  ];
  const { setModal } = useModalContext();
  const examSectionSlug = useQuery()['exam_section'] || null;
  const examSectionFromQueryString = props.mockTest.scoredExamSections.find((examSection) => (
    examSection.slug === examSectionSlug
  ));

  const [currentExamSection, setCurrentExamSection] = useState(examSectionFromQueryString || props.mockTest.scoredExamSections[0]);
  const showUpsell = props.branding.accessLevel !== 'premium';
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  const handleCurrentExamSectionChanged = (slug) => {
    const newExamSection = find(props.mockTest.scoredExamSections, (examSection) => (
      examSection.slug === slug
    ));

    window.history.replaceState(null, null, `${paths.practiceTest(props.mockTest.id)}?exam_section=${newExamSection.slug}`);
    setCurrentExamSection(newExamSection);
  };

  const shareResultModal = () => {
    const shareTestUrl = `${window.location.origin}${paths.practiceTestsShare(props.mockTest.shareableId)}`;

    const copyShareLink = async (className: string) => {
      const element = document.querySelector(`.${className}`) as HTMLElement | null;
      const textToCopy = element.textContent;
      await navigator.clipboard.writeText(textToCopy);
      element.innerText = 'Copied!';
      setTimeout(() => {
        element.innerText = textToCopy;
      }, 1000);
    };

    setModal({
      header: 'Share Your Results',
      body: (
        <div>
          <p>Sharing results will let a tutor view your test scores, performance breakdown, and answers to specific questions.</p>
          <p>
            <h5><b><u>Share link</u></b></h5>
            <div className="d-flex">
              <pre className="share-link" style={{
                whiteSpace: 'break-spaces',
                borderRadius: '8px 0 0 8px',
                borderRight: 'none',
                width: '100%'
              }}>
                {shareTestUrl}
              </pre>
              <button className="btn btn-default" type="button" onClick={() => copyShareLink('share-link')} style={{
                marginBottom: '12px',
                borderRadius: '0 8px 8px 0',
                borderWidth: '1px'
              }}>
                <FontAwesomeIcon icon='copy'></FontAwesomeIcon>
              </button>
            </div>
          </p>
          <SecondaryButton block onClick={() => setModal(null)}>Close</SecondaryButton>
        </div>
      )
    })
  }

  return (
    <div>
      {
        props.ui?.isViewingSharedResults && (
          <ViewingSharedResultsBanner
            mockTestId={props.mockTest.id}
            userName={props.mockTest.user.name}
          />
        )
      }

      <CardLayout breadcrumbs={breadcrumbs}>
        <div className="d-flex flex-column" style={{ gap: 10 }}>
          <CardLayout.Card className="d-flex flex-column flex-lg-row" style={{ gap: 20 }}>
            <div>
              <h1 className="h2 u-margin-A-n u-padding-B-xxs" style={{ lineHeight: 1.3 }}>
                <strong>Practice Test Results</strong>
              </h1>

              <div><small>Test <strong>{props.mockTest.template.name}</strong></small></div>
              <div>
                <small>
                  Finished {
                    moment(props.mockTest.finishedAt * 1000).tz(timeZone).isSame(moment().tz(timeZone), 'day')
                      ? 'Today'
                      : moment(props.mockTest.finishedAt * 1000).tz(timeZone).fromNow()
                  }
                </small>
              </div>

              {
                props.mockTest.shareableId && !props.ui?.isViewingSharedResults && (
                  <div>
                    <small>
                      <a onClick={shareResultModal}>
                        <FontAwesomeIcon icon="share"/>
                        {' '}Share your results
                      </a>
                    </small>
                  </div>
                )
              }
            </div>

            <div className="d-flex flex-1 flex-column flex-lg-row justify-content-end" style={{ gap: 15 }}>
              <ScoreCard
                name="Overall"
                noScoreReason={props.mockTest.noScoreReason}
                score={props.mockTest.score}
              />

              {
                props.mockTest.scoredExamSections.map((examSection) => (
                  <ScoreCard
                    key={examSection.slug}
                    name={examSection.name}
                    noScoreReason={examSection.noScoreReason}
                    score={examSection.score}
                  />
                ))
              }
            </div>
          </CardLayout.Card>

          <div className="d-flex flex-column-reverse flex-md-column" style={{ gap: 10 }}>
            {
              !props.ui?.isViewingSharedResults && (
                <div className="d-flex flex-grow flex-column flex-md-row" style={{ gap: 10 }}>
                  <div className={cx('d-flex flex-column flex-md-row', { 'flex-md-column': showUpsell })} style={{ gap: 10 }}>
                    <CardLayout.Card className="d-flex flex-1" style={{ gap: 30 }}>
                      <div>
                        <LightBulbPlantIcon fill={colors.purple} height={80} width={80} />
                      </div>

                      <div>
                        <div>
                          <small className="text-gray-light"><strong>NEXT STEPS</strong></small>
                        </div>

                        <div className="text-gray-darker u-margin-V-xxs">
                          <strong>Review Your Performance</strong>
                        </div>

                        <div className="text-gray-light">
                          Review your performance breakdown below to find areas where you can improve. For questions you got wrong watch our expert video explanations to see how to approach the question next time.
                        </div>
                      </div>
                    </CardLayout.Card>

                    <CardLayout.Card className="d-flex flex-1" style={{ gap: 30 }}>
                      <div>
                        <WateringSeedlingIcon fill={colors.brandPrimary} height={80} width={80} />
                      </div>

                      <div>
                        <div>
                          <small className="text-gray-light"><strong>NEXT STEPS</strong></small>
                        </div>

                        <div className="text-gray-darker u-margin-V-xxs">
                          <strong>Decide When You'll Take Your Next Test</strong>
                        </div>

                        <div className="text-gray-light">
                          Whether you are close to your goal, or still have a ways to go, we recommend taking a practice test about every 2 weeks. Set aside the time on your calendar now and commit to it!
                        </div>
                      </div>
                    </CardLayout.Card>
                  </div>

                  { showUpsell && <UpsellCard examName={props.branding.examName} saleMessage={props.branding.saleMessage} /> }
                </div>
              )
            }

            <CardLayout.Card>
              <div className="hidden-xs">
                {
                  props.mockTest.scoredExamSections.map((examSection) => (
                    <TabButton
                      disabled={examSection === currentExamSection}
                      key={examSection.slug}
                      onClick={() => handleCurrentExamSectionChanged(examSection.slug)}
                    >
                      {examSection.name}
                    </TabButton>
                  ))
                }
              </div>

              <div className="visible-xs">
                <Select
                  groupClassName="u-margin-B-n"
                  label="Exam Section"
                  onChange={(e) => handleCurrentExamSectionChanged(e.target.value)}
                  value={currentExamSection.slug}
                >
                  {
                    props.mockTest.scoredExamSections.map((examSection) => (
                      <option
                        key={examSection.slug}
                        value={examSection.slug}
                      >
                        {examSection.name}
                      </option>
                    ))
                  }
                </Select>
              </div>

              <hr className="u-margin-V-s" />

              {
                (currentExamSection.performanceBreakdown && currentExamSection.answersBySection) ? (
                  <>
                    <div>
                      <div><strong>{currentExamSection.name} Performance Breakdown</strong></div>
                      <small>Note: Some questions can appear in multiple categories</small>

                      <div className="u-margin-T-m">
                        <PerformanceBreakdown { ...currentExamSection } />
                      </div>
                    </div>

                    <hr />

                    <div className="u-margin-T-m">
                      <AnswersTable { ...currentExamSection } />
                    </div>
                  </>
                ) : (
                  <div
                    className="bg-gray-lightest border-radius-all d-flex u-border-A-s u-padding-A-m"
                    style={{ gap: 20 }}
                  >
                    <MagnifyingGlassIcon fill={colors.grayLight} />

                    <div className="d-flex flex-1 flex-column justify-content-center">
                      <strong>No Data to Display for {currentExamSection.name}</strong>
                      <span>You didn’t answer any questions in this section.</span>
                    </div>
                  </div>
                )
              }
            </CardLayout.Card>
          </div>
        </div>
      </CardLayout>
    </div>
  );
};

const WrappedShowMockTest: React.FC<Props> = (props) => {
  return (
    <ModalProvider>
      <ShowMockTest {...props}/>
    </ModalProvider>
  )
}

export default WrappedShowMockTest;
